/**
 * @fileoverview Profile "Notifications" tab body.
 * @description Lets the signed-in user see and change their notification preferences (GET and PUT
 * /notifications/preferences). Shows a skeleton while loading, an inline retry on a failed load,
 * the channel matrix once loaded, and a footer with Save / Discard that is only enabled while
 * there are unsaved toggles. A failed save shows the server's message (for example the 409/6007
 * "changed elsewhere, reloaded" text) and keeps the toggles. The profile modal receives this panel
 * as a prop from the app layer, because features may not import each other.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import NotificationPreferencesTable from '@/features/notifications/components/NotificationPreferencesTable'
import { useNotificationPreferences } from '@/features/notifications/hooks/useNotificationPreferences'
import { useNotificationPreferencesForm } from '@/features/notifications/hooks/useNotificationPreferencesForm'
import { buildPreferenceMatrix } from '@/features/notifications/utils/notification-preferences.utils'
import { ApiException } from '@/shared/api/api.exception'
import Button from '@/shared/components/atoms/Button'
import LinkButton from '@/shared/components/atoms/LinkButton'
import Skeleton from '@/shared/components/atoms/Skeleton'

import type { NotificationPreference } from '@/features/notifications/services/notification-preferences.service'

interface PreferencesFormProps {
  preferences: NotificationPreference[]
}

/**
 * @component
 * @param {PreferencesFormProps} props - The loaded rows.
 * @param {NotificationPreference[]} props.preferences - The server's latest preferences.
 */
function PreferencesForm({ preferences }: PreferencesFormProps) {
  const { t } = useTranslation()
  const form = useNotificationPreferencesForm(preferences)
  const matrix = buildPreferenceMatrix(preferences)
  const errorMessage =
    form.error instanceof ApiException ? form.error.message : t('profile.notifications.saveError')

  if (matrix.categories.length + matrix.types.length === 0) {
    return (
      <p className="text-compact text-slate" data-testid="notification-preferences-empty">
        {t('profile.notifications.empty')}
      </p>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <NotificationPreferencesTable
        matrix={matrix}
        drafts={form.drafts}
        disabled={form.isSaving}
        onToggle={form.toggle}
      />
      {form.isError && (
        <p
          role="alert"
          className="text-compact text-error"
          data-testid="notification-preferences-error"
        >
          {errorMessage}
        </p>
      )}
      {form.justSaved && (
        <p
          role="status"
          className="text-compact text-success"
          data-testid="notification-preferences-saved"
        >
          {t('profile.notifications.saved')}
        </p>
      )}
      <div className="flex items-center justify-end gap-4">
        <LinkButton
          size="sm"
          onClick={form.discard}
          disabled={form.changeCount === 0 || form.isSaving}
          data-testid="notification-preferences-discard-button"
        >
          {t('profile.notifications.discard')}
        </LinkButton>
        <Button
          size="md"
          onClick={form.save}
          isLoading={form.isSaving}
          disabled={form.changeCount === 0}
          data-testid="notification-preferences-save-button"
        >
          {form.isSaving ? t('profile.notifications.saving') : t('profile.notifications.save')}
        </Button>
      </div>
    </div>
  )
}

/**
 * @component
 */
function NotificationPreferencesPanel() {
  const { t } = useTranslation()
  const query = useNotificationPreferences()

  /**
   * @description Retries the failed load.
   */
  const retry = () => void query.refetch()

  let body
  if (query.isPending) {
    body = (
      <div className="flex flex-col gap-3" data-testid="notification-preferences-loading">
        <Skeleton height={20} />
        <Skeleton height={20} />
        <Skeleton height={20} />
      </div>
    )
  } else if (query.isError) {
    body = (
      <p
        role="alert"
        className="text-compact text-error"
        data-testid="notification-preferences-load-error"
      >
        {t('profile.notifications.loadError')}{' '}
        <LinkButton size="sm" onClick={retry} data-testid="notification-preferences-retry-button">
          {t('profile.notifications.retry')}
        </LinkButton>
      </p>
    )
  } else {
    body = <PreferencesForm preferences={query.data} />
  }

  return (
    <section className="flex flex-col gap-3" data-testid="notification-preferences-panel">
      <p className="text-compact-sm text-slate">{t('profile.notifications.hint')}</p>
      {body}
    </section>
  )
}

export default NotificationPreferencesPanel
